import { getValidatedLimitParam, getValidatedPageParam } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

export interface ISupplierData {
  id: number;
  name: string;
}

export interface ICrmSupplierContactQuotaData {
  id: number;
  supplierId: number;
  quota: number;
  remaining: number;
  used: number;
  usagePercentage: string;
  createdAt: string;
  updatedAt: string;
  supplier: ISupplierData | null;
}

export interface ICrmSupplierContactQuotasResponse {
  data: ICrmSupplierContactQuotaData[];
  total: number;
  pageCount: number;
  limit: number;
  page: number;
}

const endpoint = 'ums/v1/crm/management/suppliers/contact-quotas';
export const getCrmSupplierContactQuotasEndpointIdentifier = endpoint;

type TData = ICrmSupplierContactQuotasResponse;
type TError = AxiosError;
type TQueryKey = [
  typeof endpoint,
  {
    page: TParam;
    limit: TParam;
    supplierId?: TParam;
    minQuota?: TParam;
    maxQuota?: TParam;
    minRemaining?: TParam;
    maxRemaining?: TParam;
    sort?: TParam;
  }
];

type Options = Omit<UseQueryOptions<TData, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetCrmSupplierContactQuotasApi(options?: Options): ReturnType {
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { page, limit, supplierId, minQuota, maxQuota, minRemaining, maxRemaining, sort } = getPageParams();

  const queryKey: TQueryKey = [
    endpoint,
    {
      page,
      limit,
      supplierId,
      minQuota,
      maxQuota,
      minRemaining,
      maxRemaining,
      sort,
    },
  ];

  const queryFn = async () => {
    const params: any = {
      page: getValidatedPageParam(),
      limit: getValidatedLimitParam(),
    };

    if (supplierId) {
      params.supplierId = Number(supplierId);
    }

    if (minQuota) {
      params.minQuota = Number(minQuota);
    }

    if (maxQuota) {
      params.maxQuota = Number(maxQuota);
    }

    if (minRemaining) {
      params.minRemaining = Number(minRemaining);
    }

    if (maxRemaining) {
      params.maxRemaining = Number(maxRemaining);
    }

    if (sort) {
      params.sort = sort;
    }

    const { data } = await http.get(endpoint, { params });
    return data;
  };

  return useQueryWrapper(queryKey, queryFn, {
    enabled: isLoggedIn,
    ...options,
  });
}
